import React, { useState, useEffect } from 'react';
import designTokens from '@/design-tokens';
import { ProgressCheck } from '@/components/ui/Icons/ProgressCheck';
import { ArrowRight } from '@/components/ui/Icons/ArrowRight';
import { PrimaryButton } from '@/components/ui/Buttons/Primary';
import { TextButton } from '@/components/ui/Buttons/Text';
import Close from '@/components/ui/Icons/Close';
import { useOnboardingProgress } from '@/contexts/OnboardingProgressContext';

// ---------------------------------------------------------------------------
// Keyframes (injected once)
// ---------------------------------------------------------------------------

const STYLE_ID = 'onboarding-banner-keyframes';
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes onboardBannerSlideUp {
      from { opacity: 0; transform: translate(-50%, 30px); }
      to   { opacity: 1; transform: translate(-50%, 0); }
    }
    @keyframes onboardBannerSlideDown {
      from { opacity: 1; transform: translate(-50%, 0); }
      to   { opacity: 0; transform: translate(-50%, 30px); }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Banner Component
// ---------------------------------------------------------------------------

export const OnboardingStepCompleteBanner: React.FC = () => {
  const {
    newlyCompletedStep,
    nextStep,
    dismissBanner,
    openChecklist,
    openStep,
  } = useOnboardingProgress();

  const [exiting, setExiting] = useState(false);
  const [visible, setVisible] = useState(false);

  // Inject keyframes once
  useEffect(ensureKeyframes, []);

  // Show/hide based on newlyCompletedStep
  useEffect(() => {
    if (newlyCompletedStep) {
      setExiting(false);
      setVisible(true);
    }
  }, [newlyCompletedStep]);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      dismissBanner();
    }, 250);
  };

  // openStep, not navigate: a plan-gated next step opens the upgrade prompt instead of
  // landing on a page the plan blocks (W8).
  const handleNext = () => {
    setVisible(false);
    if (nextStep) {
      openStep(nextStep);
    } else {
      dismissBanner();
    }
  };

  const handleViewGuide = () => {
    setVisible(false);
    dismissBanner();
    openChecklist();
  };

  if (!visible || !newlyCompletedStep) return null;

  const allDone = !nextStep;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '32px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 999998,
        backgroundColor: designTokens['surface/greyscale/clear'] || '#ffffff',
        borderRadius: '16px',
        boxShadow: designTokens['elevation/lg'],
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        width: '600px',
        fontFamily: designTokens['font/family/body'] || 'Montserrat, sans-serif',
        animation: exiting
          ? 'onboardBannerSlideDown 0.25s ease-in forwards'
          : 'onboardBannerSlideUp 0.3s ease-out',
      }}
    >
      {/* Left: two rows — completed label + next step button */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
        {/* Row 1: checkmark + completed label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flexShrink: 0 }}>
            <ProgressCheck completed size={24} />
          </div>
          <span
            style={{
              fontWeight: designTokens['font/weight/medium'] || '500',
              fontSize: designTokens['font/size/md'] || '1rem',
              color: designTokens['text/primary'] || '#000000',
              whiteSpace: 'nowrap',
            }}
          >
            {newlyCompletedStep.completedLabel}
          </span>
        </div>

        {/* Row 2: next step or celebration — left-aligned under checkmark */}
        {allDone ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                fontWeight: designTokens['font/weight/medium'] || '500',
                fontSize: designTokens['font/size/sm'] || '0.875rem',
                color: designTokens['text/brand'] || '#2bb673',
                whiteSpace: 'nowrap',
              }}
            >
              All steps complete!
            </span>
          </div>
        ) : (
          <div>
            <PrimaryButton size="small" icon={<ArrowRight width={16} height={16} />} iconPosition="right" onClick={handleNext}>
              Next: {nextStep.def.label}
            </PrimaryButton>
          </div>
        )}
      </div>

      {/* Right: View Guide + close, right-aligned */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0, marginLeft: 'auto' }}>
        <TextButton size="small" onClick={handleViewGuide}>
          View Guide
        </TextButton>

        <button
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Dismiss"
        >
          <Close />
        </button>
      </div>
    </div>
  );
};
